import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["checkbox", "master", "mode", "selection", "count", "action", "stale", "selectAll", "kind", "audience", "period", "startGroup", "endGroup", "start", "end"]
  static values = { storageKey: String, total: Number, thisMonth: Array, lastMonth: Array }

  connect() {
    let saved = {}
    try { saved = JSON.parse(sessionStorage.getItem(this.storageKeyValue) || "{}") } catch (_) { /* Storage is optional. */ }
    this.all = saved.all === true
    this.selected = new Set(Array.isArray(saved.selected) ? saved.selected : [])
    this.excluded = new Set(Array.isArray(saved.excluded) ? saved.excluded : [])
    this.stale = this.hasStaleTarget && !this.staleTarget.classList.contains("d-none")
    this.refreshDates()
    this.update()
  }

  toggle(event) {
    this.setSelected(event.target.value, event.target.checked)
    this.update()
  }

  togglePage(event) {
    this.checkboxTargets.forEach(box => this.setSelected(box.value, event.target.checked))
    this.update()
  }

  setSelected(id, checked) {
    if (this.all) checked ? this.excluded.delete(id) : this.excluded.add(id)
    else checked ? this.selected.add(id) : this.selected.delete(id)
  }

  selectAll() {
    this.all = true
    this.selected.clear()
    this.excluded.clear()
    this.update()
  }

  clear() {
    this.all = false
    this.selected.clear()
    this.excluded.clear()
    this.update()
  }

  update() {
    this.checkboxTargets.forEach(box => { box.checked = this.all ? !this.excluded.has(box.value) : this.selected.has(box.value) })
    const count = this.all ? Math.max(0, this.totalValue - this.excluded.size) : this.selected.size
    const visible = this.checkboxTargets.filter(box => box.checked).length
    if (this.hasMasterTarget) {
      this.masterTarget.checked = visible > 0 && visible === this.checkboxTargets.length
      this.masterTarget.indeterminate = visible > 0 && visible < this.checkboxTargets.length
    }
    this.countTargets.forEach(target => { target.textContent = this.all ? `${count} selected across all pages` : `${count} selected` })
    this.actionTargets.forEach(button => { button.disabled = count === 0 || this.stale })
    if (this.hasModeTarget) this.modeTarget.value = this.all ? "all" : "selected"
    if (this.hasSelectionTarget) {
      this.selectionTarget.replaceChildren()
      const name = this.all ? "statement_run[excluded_ids][]" : "statement_run[client_ids][]"
      ;(this.all ? this.excluded : this.selected).forEach(id => {
        const input = document.createElement("input")
        input.type = "hidden"
        input.name = name
        input.value = id
        this.selectionTarget.appendChild(input)
      })
    }
    try { sessionStorage.setItem(this.storageKeyValue, JSON.stringify({ all: this.all, selected: [...this.selected], excluded: [...this.excluded] })) } catch (_) { /* Selection still works on this page. */ }
  }

  submitted(event) {
    if (event.detail.success) {
      try { sessionStorage.removeItem(this.storageKeyValue) } catch (_) { /* Storage is optional. */ }
    }
  }

  invalidate() {
    this.stale = true
    this.staleTargets.forEach(target => target.classList.remove("d-none"))
    this.update()
  }

  changeKind() {
    this.audienceTarget.value = this.kindTarget.value === "activity" ? "activity" : "outstanding"
    this.refreshDates()
  }

  refreshDates() {
    const activity = this.kindTarget.value === "activity" || this.audienceTarget.value === "activity"
    const custom = this.periodTarget.value === "custom"
    this.startGroupTarget.hidden = !custom || !activity
    this.endGroupTarget.hidden = !custom
    this.startTarget.required = custom && activity
  }

  changePeriod() {
    const dates = { this_month: this.thisMonthValue, last_month: this.lastMonthValue }[this.periodTarget.value]
    if (dates) [this.startTarget.value, this.endTarget.value] = dates
    this.refreshDates()
    if (!dates) (this.startGroupTarget.hidden ? this.endTarget : this.startTarget).focus()
  }

  customPeriod() {
    this.periodTarget.value = "custom"
    this.refreshDates()
  }
}
